<template>
<div class="page" v-if="metaphorRows.length > 0 && ready">
  <a name="metaphors"/>
    <p class="dustin-title">
    YourImpact Metaphor Metrics
  </p>
  <div class="columns">
    <div class="column is-seven-eighths">
      <p class="description">
        Methodology
      </p>
      <p class="criteria">
        Each metaphor represents how the dollar weighted impacts of your fund holdings compare to
        <template v-if="benchmark.external_id">
          your chosen benchmark.
        </template>
        <template v-else>
          the total US stock market index, to represent a societal average.
        </template>
      </p>
      <p class="criteria">
        The relevant metrics are calculated for the portfolio and the benchmark on the basis of ownership percentage of each company. For example, a portfolio that owns 1% of a company that slaughters 1,000,000 chickens is proportionally responsible for 10,000 chickens slaughtered.
      </p>

      <template
        v-if="holdings.total_dollars <= 10000000"
        >
          <p class="criteria" >
            In order to make output more readable, for portfolios with under $10 million in assets covered by YourStake.org, the portfolio value is scaled up to $10 million to calculate annual metrics, and displayed as $1 million over 10 years, keeping holdings and company activities constant. For example, a portfolio with $300 in Company A and $200 in Company B is scaled up to a portfolio with $6 million in Company A and $4 million in Company B, and associated metaphors reflect the annual metrics associated with this scaled up portfolio. 
          </p>
      </template>
    </div>

  </div>
  <div style="margin-top: -1rem">
    <template
      v-if="holdings.total_dollars > 10000000"
    >
      <p class="preheader">
        By investing
        {{ bigDollarFormat(holdings.total_dollars, 1).toUpperCase() }}
        in your portfolio for one year, you may be responsible for the equivalent of approximately:
      </p>
    </template>
    <template
      v-else
    >
      <p class="preheader smaller-line-height has-text-centered">
        If you invest
        $1M
        in your portfolio over 10 years, you may be responsible for the equivalent of approximately:
      </p>
    </template>
  </div>

  <div
    v-if="ready"
    class="metaphor-tiles"
  >
  <div class="print-no-break metaphorcontainer"
    v-for="(row, rowIndex) in metaphorRows"
    :key="'row-' + rowIndex"
  >
    <div
      v-for="(metaphor, metaphorIndex) in row"
      :key="'metaphor-' + rowIndex + '-' + metaphorIndex"
      class="metaphor-tile has-text-centered print-no-break"
    >
      <a
        target="_blank" rel="noreferrer noopener"
        v-bind:href="`/metaphor-report-attribution/${metaphor.metaphor_id}/${portfolio.slug}${benchmark.name ? ':' + benchmark.slug : ''}`"
      >
        <span>
          <p class="title is-5">
            {{metaphor.metaphor}}
          </p>
          <p class="highlights">
            {{
              metaphor.metaphor_number.toLocaleString(undefined, {maximumFractionDigits: 0})
              }}
          </p>
        </span>
      </a>
      <span>
        <p class="description">
          {{metaphor.name}}
        </p>
      
        <span class="icon is-large">
          <i class="fas fa-lg" :class="metaphor.icon"></i>
        </span>
      </span>
      <p class="criteria">
        {{metaphor.description}}
      </p>
    </div>
  </div>

</div><!-- page-->

</div>
</template>

<script>
  import BarChart from './subcomponents/BarChart.js'

  import eventBus from '@frontend/shared/EventBus.js';
  import { bigDollarFormat } from '@frontend/shared/Text.js';
  import { apiFetch } from '../../../api/api';

  export default {
    data () {
      return {
        ready: false,
        metaphors: [],
        holdings: {},
        benchmarkMetaphors: {},
        metaphorComparisonOptions: {},
        metaphorUpdateTimeout: null,
        hasUpdatedMetaphors: false,
      }
    },
    name: 'Metaphors',
    props: {
      portfolio: Object,
      benchmark: Object,
    },
    created: async function() {
      let portfolioExternalId = this.benchmark.external_id ? `${this.portfolio.external_id}:${this.benchmark.external_id}` : this.portfolio.external_id;

      const currentParams = new URLSearchParams(window.location.search);
      if (currentParams.get('metaphor_section')) {
        let shouldRender = currentParams.get('metaphor_section');
        if (shouldRender === 'false' || shouldRender === '0') { // TODO: Section enable/disable should have unified querystring api
          return;
        }
      }

      let newParams = {};
      if (currentParams.get('m')) {
        newParams.metaphors = currentParams.get('m');
      }
      let response = await apiFetch(`/api/v1/portfolios/${portfolioExternalId}/report/metaphor/?${new URLSearchParams(newParams).toString()}`);
      let data = await response.json();
      this.metaphors.push(...data.report.metrics);
      this.holdings.total_dollars = data.report.holdings.total_dollars;

      this.metaphors.forEach((itm) => {
        if (Math.abs(Math.round(itm.metaphor_number)) > 0) {
          this.ready = true;
        }
      });

      if (this.metaphorRows.length > 0) {
        this.ready = true;
        eventBus.$emit('InstitutionalReport:page-rendered', this.$options.name);
      }
    },
    mounted () {
      window.addEventListener('message', this.handleMessage);
      this.sendMessage({ mounted: 'Metaphors.vue' })
      this.fillData()
    },
    beforeDestroy() {
      window.removeEventListener('message', this.handleMessage);
    },
    components: {BarChart},
    computed: {
      metaphorsWithNumber() {
        if(!this.metaphors?.length) return [];

        return this.metaphors.filter((m) => {
          const number = Math.round(m.metaphor_number || 0);
          return Math.abs(number) > 0;
        })
      },
      metaphorRows: function() {
        if(!this.ready) return [];
        return this.metaphorsWithNumber.reduce((acc, itm) => {
          let last = acc.slice(-1);
          if (last.length > 0 && last[0].length < 3) {
            acc[acc.length - 1].push(itm);
          }
          else {
            acc.push([itm]);
          }
          return acc;
        }, []);
      },
      portfolioID() {
        const benchmark = this.benchmark?.external_id;
        if(!benchmark) return this.portfolio.external_id;

        return `${this.portfolio.external_id}:${this.benchmark.external_id}`;
      },
    },
    watch: {
      metaphorRows(newMetaphors, old) {
        this.hasUpdatedMetaphors = false;
        this.ready = true;
        eventBus.$emit('InstitutionalReport:page-rendered', this.$options.name);
      }
    },
    methods: {
      bigDollarFormat: bigDollarFormat,
      fillData() {
        this.metaphorComparisonOptions = {
          legend: {display: false}
        }
      },
      handleMessage(event) {
        // Handle message from parent when inside an iframe
        const data = event.data;
        if(data.event === 'metaphors-changed') this.scheduleMetaphorUpdate(data);
      },
      sendMessage(data) {
        // Send message to parent from inside the iframe
        window.parent.postMessage(data, '*');
      },
      scheduleMetaphorUpdate(data) {
        clearTimeout(this.metaphorUpdateTimeout);
        this.metaphorUpdateTimeout = setTimeout(() => { this.updateMetaphors(data) }, 250);
      },
      async updateMetaphors({ metaphors }) {
        console.log('Metaphors.vue updating metaphors')
        const newReportData = await this.getMetaphorData(metaphors);
        this.hasUpdatedMetaphors = true;

        this.metaphors = newReportData.metrics;
        this.holdings.total_dollars = newReportData.holdings.total_dollars;
      },
      async getMetaphorData(ids) {
        if(!this.portfolioID || !ids?.length) return [];

        const url = [
          `/api/v1/portfolios/${this.portfolioID}`,
          '/report/metaphor/?',
          `metaphors=${ids.join(',')}`
        ];
        const res  = await apiFetch(url.join(''));
        const data = await res.json();

        return data.report;
      }
    }
  }
</script>

<style lang="css" scoped>
  .tableofcontents {
    padding: 6rem;
  }

  .metaphor-tiles {
    justify-content: flex-start;
    flex-wrap: wrap;
    width: 100%;
  }
  .metaphor-tile {
    border-radius: 3px;
    border: 2px solid var(--black-95);
    padding: 1rem;
    width: 15rem;
    /* TODO: This height is arbitrary and should probably not be static since it risks overflow given the variety of
    potential content. */
    height: 20rem;
    margin: .5rem;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
  }
  .title.is-5 {
    margin-bottom: .75rem;
  }
  .metaphorcontainer {
    display: flex;
    break-inside: avoid;
    -webkit-column-break-inside: avoid;
    page-break-inside: avoid;
  }
  p {
    margin-bottom: .5rem;
  }
  .csscolumns {
    column-count: 3;
    column-gap: 1em;
    column-rule-style: none;
    column-rule-width: 1px;
    column-rule-color: var(--black-95);
    column-fill: auto;
    -webkit-column-break-inside: avoid;
    page-break-inside: avoid;
    break-inside: avoid;
  }
</style>
